import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { safeFormatDate } from '../lib/dateTime';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import {
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  ArrowDownRight,
  Banknote,
  X,
  AlertCircle,
  CalendarClock,
  ChevronLeft,  
  ChevronRight,
  ChevronDown,
  PieChart,
  BarChart3,
  Calendar,
  Palmtree,
  Info,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { formatHoursToHM } from '../lib/formatTime';
import { OwnMonthlyReportMenu } from '../components/reports/MonthlyReportsDialog';

// ==================== SVG Ring Component ====================
function AccountRing({ balance, capHours, committedInBalance, freeHours, weeklyHours }) {
  const size = 180;
  const strokeWidth = 16;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;
  
  // Lower cap: vorläufig weekly_hours / 2 (später aus Kappungsregel M3)
  const lowerCap = weeklyHours / 2;
  
  const isPositive = balance > 0;
  const isNegative = balance < 0;
  const isZero = balance === 0;
  const isOverCap = balance > capHours;
  const isUnderLowerCap = balance < -lowerCap;
  
  // Calculate segments
  let segments = [];
  let centerText = { value: '0 h', label: 'Ausgeglichen', subLabel: null };
  let fillColor = 'stroke-muted';
  
  if (isPositive) {
    const scale = capHours;
    const freeAngle = Math.min(1, freeHours / scale);
    const committedAngle = Math.min(1 - freeAngle, committedInBalance / scale);
    
    // Free segment (emerald) - starts at 12 o'clock
    segments.push({
      offset: 0,
      length: freeAngle,
      color: 'stroke-emerald-500',
    });
    // Committed segment (indigo)
    if (committedInBalance > 0) {
      segments.push({
        offset: freeAngle,
        length: committedAngle,
        color: 'stroke-indigo-500',
      });
    }
    
    centerText = {
      value: formatHoursToHM(balance, true),
      label: 'Guthaben',
      subLabel: `von max. ${capHours} h`,
      colorClass: 'text-emerald-600 dark:text-emerald-400',
    };
  } else if (isNegative) {
    const scale = lowerCap;
    const fillRatio = Math.min(1, Math.abs(balance) / scale);
    
    // Orange segment - counter-clockwise from 12 o'clock
    segments.push({
      offset: 0,
      length: fillRatio,
      color: 'stroke-orange-500',
      counterClockwise: true,
    });
    
    centerText = {
      value: `−${formatHoursToHM(Math.abs(balance))}`,
      label: 'Minusstunden',
      subLabel: `erlaubt bis −${lowerCap} h`,
      colorClass: 'text-orange-600 dark:text-orange-400',
    };
  } else {
    centerText = {
      value: '0 h',
      label: 'Ausgeglichen',
      subLabel: null,
      colorClass: 'text-muted-foreground',
    };
  }
  
  // Start at 12 o'clock (-90 degrees)
  const startAngle = -90;
  
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className="stroke-muted/30"
        />
        
        {/* Segments */}
        {segments.filter(seg => seg.length > 0).map((seg, i) => {
          const dashLength = seg.length * circumference;
          const dashOffset = seg.counterClockwise
            ? circumference - seg.offset * circumference
            : -seg.offset * circumference;
          
          return (
            <circle
              key={i}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              strokeWidth={strokeWidth}
              className={seg.color}
              strokeDasharray={`${dashLength} ${circumference - dashLength}`}
              strokeDashoffset={dashOffset}
              strokeLinecap="butt"
              style={{
                transform: seg.counterClockwise ? 'scaleY(-1)' : undefined,
                transformOrigin: 'center',
              }}
            />
          );
        })}
      </svg>
      
      {/* Center text overlay */}
      <div className="absolute flex flex-col items-center justify-center" style={{ width: size, height: size }}>
        <span className={`text-2xl font-bold ${centerText.colorClass}`}>
          {centerText.value}
        </span>
        <span className="text-sm text-muted-foreground">{centerText.label}</span>
        {centerText.subLabel && (
          <span className="text-xs text-muted-foreground mt-1">{centerText.subLabel}</span>
        )}
      </div>
    </div>
  );
}

// ==================== Small Progress Ring ====================
function SmallRing({ progress, size = 48, strokeWidth = 4, colorClass = 'stroke-indigo-500' }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashLength = (progress / 100) * circumference;
  const center = size / 2;
  
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        strokeWidth={strokeWidth}
        className="stroke-muted/30"
      />
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        strokeWidth={strokeWidth}
        className={colorClass}
        strokeDasharray={`${dashLength} ${circumference - dashLength}`}
        strokeLinecap="round"
      />
    </svg>
  );
}

// ==================== Progress Bar ====================
function ProgressBar({ progress, colorClass = 'bg-indigo-500' }) {
  return (
    <div className="w-full h-2 bg-muted/30 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${colorClass}`}
        style={{ width: `${Math.min(100, progress)}%` }}
      />
    </div>
  );
}

// ==================== Skeleton Loader ====================
function Skeleton({ className }) {
  return <div className={`animate-pulse bg-muted/50 rounded ${className}`} />;
}

// ==================== Main Component ====================
export default function MyTimeAccountPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isLoading, setIsLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  
  // Summary data from new endpoint
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState(null);
  
  // Preferences state (optimistic)
  const [preferences, setPreferences] = useState({ week_chart: 'ring', month_chart: 'ring' });
  
  // Details section collapsed state
  const [detailsOpen, setDetailsOpen] = useState(false);
  
  // History view mode
  const [historyView, setHistoryView] = useState('month'); // 'month' | 'year'
  
  // Legacy data for existing sections
  const [overtimeBalance, setOvertimeBalance] = useState(null);
  const [committedOvertime, setCommittedOvertime] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  
  // Request dialog state
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requestType, setRequestType] = useState('reduction');
  const [requestHours, setRequestHours] = useState('');
  const [requestNote, setRequestNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  // Fetch new summary data
  const fetchSummary = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get(`/time-account/me/summary?year=${selectedYear}`);
      setSummary(res.data);
      setPreferences(res.data.preferences || { week_chart: 'ring', month_chart: 'ring' });
    } catch (error) {
      console.error('Failed to fetch summary:', error);
      toast.error('Fehler beim Laden der Kontodaten');
    } finally {
      setIsLoading(false);
    }
  }, [api, selectedYear]);

  // Fetch history data
  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get(`/time-account/me/history?year=${selectedYear}`);
      setHistory(res.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [api, selectedYear]);

  // Fetch legacy overtime data (for existing sections)
  const fetchLegacyData = useCallback(async () => {
    try {
      const [overtimeRes, committedRes] = await Promise.all([
        api.get(`/overtime/ledger?year=${selectedYear}`),
        api.get('/overtime/committed'),
      ]);
      setOvertimeBalance(overtimeRes.data.balance || null);
      setCommittedOvertime(committedRes.data);
    } catch (error) {
      console.error('Failed to fetch legacy data:', error);
    }
  }, [api, selectedYear]);

  // Fetch my requests
  const fetchMyRequests = useCallback(async () => {
    try {
      const response = await api.get('/overtime/requests/my');
      setMyRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchSummary();
    fetchHistory();
    fetchLegacyData();
    fetchMyRequests();
  }, [fetchSummary, fetchHistory, fetchLegacyData, fetchMyRequests]);

  // When year changes, reset history view if not current year
  useEffect(() => {
    if (selectedYear !== currentYear) {
      setHistoryView('year');
    }
  }, [selectedYear, currentYear]);

  // Monatskachel: Monat wählbar (Standard: aktueller Monat, in anderen Jahren Dezember)
  const currentMonth = new Date().getMonth() + 1;
  const maxMonth = selectedYear === currentYear ? currentMonth : 12;
  const [viewMonth, setViewMonth] = useState(currentMonth);
  const [monthData, setMonthData] = useState(null);

  useEffect(() => {
    setViewMonth(selectedYear === currentYear ? currentMonth : 12);
  }, [selectedYear, currentYear, currentMonth]);

  useEffect(() => {
    // Der aktuelle Monat kommt aus der Zusammenfassung, alle anderen werden nachgeladen
    if (selectedYear === currentYear && viewMonth === currentMonth) {
      setMonthData(null);
      return undefined;
    }
    let cancelled = false;
    setMonthData(null);
    api.get(`/time-account/me/month?year=${selectedYear}&month=${viewMonth}`)
      .then((res) => { if (!cancelled) setMonthData(res.data); })
      .catch(() => { if (!cancelled) toast.error('Monat konnte nicht geladen werden'); });
    return () => { cancelled = true; };
  }, [api, selectedYear, viewMonth, currentYear, currentMonth]);  

  // Save preferences
  const savePreferences = async (newPrefs) => {
    const oldPrefs = { ...preferences };
    setPreferences(newPrefs); // Optimistic update
    
    try {
      await api.put('/time-account/me/preferences', newPrefs);
    } catch (error) {
      setPreferences(oldPrefs); // Rollback
      toast.error('Fehler beim Speichern der Einstellungen');
    }
  };

  const setWeekChart = (type) => {
    if (preferences.week_chart === type) return; // Bereits gesetzt, kein PUT
    savePreferences({ ...preferences, week_chart: type });
  };

  const setMonthChart = (type) => {
    if (preferences.month_chart === type) return; // Bereits gesetzt, kein PUT
    savePreferences({ ...preferences, month_chart: type });
  };

  // Submit request handler
  const handleSubmitRequest = async () => {
    if (!requestHours || parseFloat(requestHours) <= 0) {
      toast.error('Bitte geben Sie gültige Stunden ein');
      return;
    }

    const hours = parseFloat(requestHours);
    const available = summary?.account?.balance ?? 0;
    
    if (hours > available) {
      toast.error(`Nicht genügend Zeitguthaben. Verfügbar: ${available.toFixed(2)}h`);
      return;
    }

    setIsSubmitting(true);
    try {
      const endpoint = requestType === 'reduction' ? '/overtime/request-reduction' : '/overtime/request-payout';
      await api.post(`${endpoint}?hours=${hours}${requestNote ? `&note=${encodeURIComponent(requestNote)}` : ''}`);
      
      toast.success(requestType === 'reduction' 
        ? 'Antrag auf Zeitausgleich eingereicht' 
        : 'Antrag auf Auszahlung von Zeitguthaben eingereicht'
      );
      
      setShowRequestDialog(false);
      setRequestHours('');
      setRequestNote('');
      fetchMyRequests();
      fetchSummary();
      fetchLegacyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Einreichen des Antrags');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cancel pending request
  const handleCancelRequest = async (requestId) => {
    try {
      await api.delete(`/overtime/requests/${requestId}`);
      toast.success('Antrag storniert');
      fetchMyRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Stornieren');
    }
  };

  // Open request dialog
  const openRequestDialog = (type) => {
    setRequestType(type);
    setRequestHours('');
    setRequestNote('');
    setShowRequestDialog(true);
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending': return <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-400">Ausstehend</span>;
      case 'approved': return <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-500/20 text-emerald-400">Genehmigt</span>;
      case 'rejected': return <span className="px-2 py-0.5 text-xs rounded-full bg-orange-500/20 text-orange-400">Abgelehnt</span>;
      default: return null;
    }
  };

  // Derived values
  const account = summary?.account || {};
  const isCurrentYear = summary?.is_current_year ?? true;
  const isViewingCurrentMonth = isCurrentYear && viewMonth === currentMonth;
  const shownMonth = isViewingCurrentMonth ? summary?.month : monthData;  
  const weeklyHours = summary?.weekly_hours ?? 40;
  const lowerCap = weeklyHours / 2; // vorläufig, später aus Kappungsregel M3
  
  const balance = account.balance ?? 0;
  const capHours = account.cap_hours ?? weeklyHours;
  const isOverCap = balance > capHours;
  const isUnderLowerCap = balance < -lowerCap;
  
  const showCommittedTile = balance > 0 || (account.committed_hours ?? 0) > 0;
  const hasLedgerAdjustments = overtimeBalance && (
    overtimeBalance.ledger_reduction !== 0 || 
    overtimeBalance.ledger_payout !== 0 || 
    overtimeBalance.ledger_correction !== 0
  );
  
  const pendingRequests = myRequests.filter(r => r.status === 'pending');
  const completedRequests = myRequests.filter(r => r.status !== 'pending');

  // Format number for display (e.g. 1.5 -> "1,5")
  const formatDays = (days) => {
    if (days === Math.floor(days)) return days.toString();
    return days.toFixed(1).replace('.', ',');
  };

  // Chart data preparation
  const chartData = useMemo(() => {
    if (!history) return { daily: [], monthly: [] };
    
    const daily = (history.daily || []).map(d => ({
      date: d.date,
      label: safeFormatDate(d.date, 'd.'),
      balance: d.balance,
    }));
    
    const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    const monthly = (history.monthly || []).map(m => ({
      month: m.month,
      label: monthNames[m.month - 1],
      balance: m.end_balance,
      isPartial: m.is_partial,
    }));
    
    return { daily, monthly };
  }, [history]);

  // Calculate gradient stop for chart (0-crossing)
  const gradientOffset = useMemo(() => {
    const data = historyView === 'month' ? chartData.daily : chartData.monthly;
    const validData = data.filter(d => d.balance !== null);
    if (validData.length === 0) return 0.5;
    
    const max = Math.max(...validData.map(d => d.balance));
    const min = Math.min(...validData.map(d => d.balance));
    
    if (max <= 0) return 0;
    if (min >= 0) return 1;
    
    return max / (max - min);
  }, [chartData, historyView]);

  // Status line logic
  const getStatusInfo = () => {
    if (balance >= 0 && balance <= capHours) {
      return {
        icon: TrendingUp,
        title: 'Alles im grünen Bereich',
        description: 'Dein Arbeitszeitkonto liegt im vorgesehenen Rahmen.',
        colorClass: 'text-emerald-600 dark:text-emerald-400',
        bgClass: 'bg-emerald-500/10 border-emerald-500/20',
      };
    }
    if (balance > capHours) {
      return {
        icon: TrendingUp,
        title: 'Guthaben über der Obergrenze',
        description: 'Plane einen Zeitausgleich ein.',
        colorClass: 'text-indigo-600 dark:text-indigo-400',
        bgClass: 'bg-indigo-500/10 border-indigo-500/20',
      };
    }
    if (balance >= -lowerCap && balance < 0) {
      return {
        icon: Minus,
        title: 'Leichtes Minus',
        description: `Im erlaubten Rahmen bis −${lowerCap} h.`,
        colorClass: 'text-orange-600 dark:text-orange-400',
        bgClass: 'bg-orange-500/10 border-orange-500/20',
      };
    }
    return {
      icon: TrendingDown,
      title: 'Untergrenze überschritten',
      description: 'Bitte stimme dich mit deiner Vorgesetzten ab.',
      colorClass: 'text-orange-600 dark:text-orange-400',
      bgClass: 'bg-orange-500/20 border-orange-500/30',
    };
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const value = payload[0].value;
    if (value === null) return null;
    
    const isPartial = payload[0].payload?.isPartial;
    const sign = value >= 0 ? '+' : '−';
    const formatted = formatHoursToHM(Math.abs(value));
    
    return (
      <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className={`text-sm font-medium ${value >= 0 ? 'text-emerald-500' : 'text-orange-500'}`}>
          {sign}{formatted}
        </p>
        {isPartial && (
          <p className="text-xs text-muted-foreground">Stand gestern</p>
        )}
      </div>
    );
  };

  // Loading skeleton
  if (isLoading && !summary) {
    return (
      <div data-testid="my-time-account-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-48" />
          </div>
          <Skeleton className="h-10 w-[120px]" />
        </div>
        <Skeleton className="h-64 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="my-time-account-page" className="space-y-6">
      {/* A) Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
            Mein Arbeitszeitkonto
          </h1>
          <p className="text-muted-foreground mt-1">
            Dein aktueller Stand auf einen Blick
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* F11/F12: eigene Monatsauswertungen des angezeigten Monats (Monat in der Monatskachel wählen) */}
          <OwnMonthlyReportMenu api={api} year={selectedYear} month={viewMonth} />
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[120px] bg-input border-border text-foreground">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              {years.map((year) => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* B) + C) + D) Account Card with Tiles */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-glass p-6"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* B) Account Ring */}
          <div className="flex flex-col items-center">
            <div className="relative">
              <AccountRing
                balance={balance}
                capHours={capHours}
                committedInBalance={account.committed_in_balance_hours ?? 0}
                freeHours={account.free_hours ?? 0}
                weeklyHours={weeklyHours}
              />
            </div>
            
            {/* Legend (only positive balance with committed) */}
            {balance > 0 && (account.committed_hours ?? 0) > 0 && (
              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-xs text-muted-foreground">frei</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-indigo-500" />
                  <span className="text-xs text-muted-foreground">verplant</span>
                </div>
              </div>
            )}
            
            {/* Over cap warning */}
            {isOverCap && (
              <p className="text-sm text-muted-foreground mt-4 text-center">
                {formatHoursToHM(account.over_cap_hours ?? 0, true)} über der Obergrenze – Abbau einplanen
              </p>
            )}
            
            {/* Under lower cap warning */}
            {isUnderLowerCap && (
              <div className="mt-4 p-3 rounded-lg bg-orange-500/10 border border-orange-500/30 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-orange-700 dark:text-orange-300 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-orange-700 dark:text-orange-300">
                  Untergrenze von −{lowerCap} h überschritten – bitte mit deiner Vorgesetzten abstimmen.
                </p>
              </div>
            )}

            {/* Details section (collapsible) */}
            {hasLedgerAdjustments && (
              <div className="w-full mt-4 pt-4 border-t border-border">
                <button
                  type="button"
                  onClick={() => setDetailsOpen(!detailsOpen)}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full justify-center"
                >
                  <span>Details</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${detailsOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {detailsOpen && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs text-muted-foreground text-center">Berücksichtigte Anpassungen</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {overtimeBalance?.ledger_reduction !== 0 && (
                        <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-500/10 rounded">
                          <ArrowDownRight className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                          <span className="text-xs text-blue-600 dark:text-blue-400">Abbau: {overtimeBalance.ledger_reduction}h</span>
                        </div>
                      )}
                      {overtimeBalance?.ledger_payout !== 0 && (
                        <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-500/10 rounded">
                          <Banknote className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                          <span className="text-xs text-amber-600 dark:text-amber-400">Auszahlung: {overtimeBalance.ledger_payout}h</span>
                        </div>
                      )}
                      {overtimeBalance?.ledger_correction !== 0 && (
                        <div className="flex items-center gap-1.5 px-2 py-1 bg-purple-500/10 rounded">
                          <AlertCircle className="w-3.5 h-3.5 text-purple-400" />
                          <span className="text-xs text-purple-400">
                            Korrektur: {overtimeBalance.ledger_correction > 0 ? '+' : ''}{overtimeBalance.ledger_correction}h
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* C) + D) Tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Week Tile */}
            {isCurrentYear && summary?.week && (
              <div className="p-4 rounded-lg bg-muted/30 border border-border">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-foreground">Diese Woche</h3>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => setWeekChart('ring')}
                      className={`p-1 rounded ${preferences.week_chart === 'ring' ? 'bg-indigo-500/20 text-indigo-400' : 'text-muted-foreground hover:text-foreground'}`}
                      aria-label="Ring-Diagramm"
                    >
                      <PieChart className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setWeekChart('bar')}
                      className={`p-1 rounded ${preferences.week_chart === 'bar' ? 'bg-indigo-500/20 text-indigo-400' : 'text-muted-foreground hover:text-foreground'}`}
                      aria-label="Balkendiagramm"
                    >
                      <BarChart3 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {summary.week.target_hours === 0 && summary.week.actual_hours === 0 ? (
                  <p className="text-sm text-muted-foreground">Kein Soll in diesem Zeitraum</p>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      {preferences.week_chart === 'ring' ? (
                        <SmallRing
                          progress={summary.week.progress_pct}
                          colorClass={summary.week.progress_pct >= 100 ? 'stroke-emerald-500' : 'stroke-indigo-500'}
                        />
                      ) : (
                        <div className="flex-1">
                          <ProgressBar
                            progress={summary.week.progress_pct}
                            colorClass={summary.week.progress_pct >= 100 ? 'bg-emerald-500' : 'bg-indigo-500'}
                          />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-lg font-semibold text-foreground">
                          {formatHoursToHM(summary.week.actual_hours)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          von {formatHoursToHM(summary.week.target_hours)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-2 space-y-1">
                      {summary.week.over_hours > 0 && (
                        <p className="text-xs text-emerald-600 dark:text-emerald-400">
                          {formatHoursToHM(summary.week.over_hours, true)}
                        </p>
                      )}
                      {summary.week.absence_days > 0 && (
                        <p className="text-xs text-muted-foreground">
                          ({summary.week.absence_days} Abwesenheitstage berücksichtigt)
                        </p>
                      )}
                      {summary.week.running_session_hours > 0 && (
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                          <span className="text-xs text-muted-foreground">inkl. laufender Sitzung</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Month Tile – Monat wählbar */}
            {summary && (
              <div className="p-4 rounded-lg bg-muted/30 border border-border">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setViewMonth((m) => Math.max(1, m - 1))}
                      disabled={viewMonth <= 1}
                      className="p-1 rounded text-muted-foreground hover:text-foreground disabled:opacity-30"
                      aria-label="Vormonat"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <h3 className="text-sm font-medium text-foreground min-w-[9rem] text-center">
                      {isViewingCurrentMonth ? 'Dieser Monat' : 'Monat'} ({safeFormatDate(`${selectedYear}-${String(viewMonth).padStart(2, '0')}-01`, 'MMMM')})
                    </h3>
                    <button
                      type="button"
                      onClick={() => setViewMonth((m) => Math.min(maxMonth, m + 1))}
                      disabled={viewMonth >= maxMonth}
                      className="p-1 rounded text-muted-foreground hover:text-foreground disabled:opacity-30"
                      aria-label="Nächster Monat"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => setMonthChart('ring')}
                      className={`p-1 rounded ${preferences.month_chart === 'ring' ? 'bg-indigo-500/20 text-indigo-400' : 'text-muted-foreground hover:text-foreground'}`}
                      aria-label="Ring-Diagramm"
                    >
                      <PieChart className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setMonthChart('bar')}
                      className={`p-1 rounded ${preferences.month_chart === 'bar' ? 'bg-indigo-500/20 text-indigo-400' : 'text-muted-foreground hover:text-foreground'}`}
                      aria-label="Balkendiagramm"
                    >
                      <BarChart3 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {!shownMonth ? (
                  <Skeleton className="h-12 w-full" />
                ) : shownMonth.target_hours === 0 && shownMonth.actual_hours === 0 ? (
                  <p className="text-sm text-muted-foreground">Kein Soll in diesem Zeitraum</p>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      {preferences.month_chart === 'ring' ? (
                        <SmallRing
                          progress={shownMonth.progress_pct}
                          colorClass={shownMonth.progress_pct >= 100 ? 'stroke-emerald-500' : 'stroke-indigo-500'}
                        />
                      ) : (
                        <div className="flex-1">
                          <ProgressBar
                            progress={shownMonth.progress_pct}
                            colorClass={shownMonth.progress_pct >= 100 ? 'bg-emerald-500' : 'bg-indigo-500'}
                          />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-lg font-semibold text-foreground">
                          {formatHoursToHM(shownMonth.actual_hours)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          von {formatHoursToHM(shownMonth.target_hours)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-2 space-y-1">
                      {shownMonth.actual_hours !== shownMonth.target_hours && !isViewingCurrentMonth && (
                        <p className={`text-xs ${shownMonth.actual_hours >= shownMonth.target_hours ? 'text-emerald-600 dark:text-emerald-400' : 'text-orange-600 dark:text-orange-400'}`}>
                          {formatHoursToHM(shownMonth.actual_hours - shownMonth.target_hours, true)} im Monat
                        </p>
                      )}
                      {isViewingCurrentMonth && shownMonth.over_hours > 0 && (
                        <p className="text-xs text-emerald-600 dark:text-emerald-400">
                          {formatHoursToHM(shownMonth.over_hours, true)}
                        </p>
                      )}
                      {shownMonth.absence_days > 0 && (
                        <p className="text-xs text-muted-foreground">
                          ({shownMonth.absence_days} Abwesenheitstage berücksichtigt)
                        </p>
                      )}
                      {shownMonth.running_session_hours > 0 && (
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                          <span className="text-xs text-muted-foreground">inkl. laufender Sitzung</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* D) Committed/Free Tile */}
            {showCommittedTile && (
              <div className="p-4 rounded-lg bg-muted/30 border border-border sm:col-span-2">
                <h3 className="text-sm font-medium text-foreground mb-3">Verplant / Frei</h3>
                
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">
                    Zum Abbau verplant: <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                      {formatHoursToHM(account.committed_hours ?? 0)}
                    </span>
                  </span>
                  <span className="text-muted-foreground">
                    Noch frei: <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                      {formatHoursToHM(account.free_hours ?? 0)}
                    </span>
                  </span>
                </div>
                
                {/* Split bar */}
                <div className="h-3 rounded-full overflow-hidden flex bg-muted/30">
                  {(account.committed_in_balance_hours ?? 0) > 0 && (
                    <div
                      className="bg-indigo-500 h-full"
                      style={{
                        width: `${Math.min(100, ((account.committed_in_balance_hours ?? 0) / Math.max(1, balance)) * 100)}%`
                      }}
                    />
                  )}
                  {(account.free_hours ?? 0) > 0 && (
                    <div
                      className="bg-emerald-500 h-full"
                      style={{
                        width: `${Math.min(100, ((account.free_hours ?? 0) / Math.max(1, balance)) * 100)}%`
                      }}
                    />
                  )}
                </div>
                
                {(account.committed_hours ?? 0) > balance && balance > 0 && (
                  <div className="mt-2 flex items-center gap-1.5 text-orange-600 dark:text-orange-400">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span className="text-xs">Mehr verplant als Guthaben vorhanden – es entstehen Minusstunden.</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* E) + F) Vacation and Next Absence */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* E) Vacation */}
        {summary?.vacation && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onClick={() => navigate('/leave')}
            className="card-glass p-5 cursor-pointer hover:border-indigo-500/30 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                  <Palmtree className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Resturlaub</h3>
                  <p className="text-2xl font-bold text-foreground">
                    {formatDays(summary.vacation.remaining_total)} Tage
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
            </div>
            
            <div className="mt-3 space-y-1 text-xs text-muted-foreground">
              {summary.vacation.remaining_carryover > 0 && (
                <p>
                  inkl. {formatDays(summary.vacation.remaining_carryover)} Tage Übertrag aus {selectedYear - 1}
                  {summary.vacation.carryover_expiry_date && (
                    <span> (verfällt am {safeFormatDate(summary.vacation.carryover_expiry_date, 'd. MMM yyyy')})</span>
                  )}
                </p>
              )}
              {summary.vacation.pending_days > 0 && (
                <p>{formatDays(summary.vacation.pending_days)} Tage beantragt</p>
              )}
            </div>
          </motion.div>
        )}

        {/* F) Next Absence */}
        {isCurrentYear && summary?.next_absence && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            onClick={() => navigate('/calendar')}
            className="card-glass p-5 cursor-pointer hover:border-indigo-500/30 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Nächste Abwesenheit</h3>
                  {summary.next_absence.is_ongoing ? (
                    <p className="text-foreground font-medium">
                      {summary.next_absence.label} – läuft bis {safeFormatDate(summary.next_absence.end_date, 'd. MMM yyyy')}
                    </p>
                  ) : (
                    <p className="text-foreground font-medium">
                      {summary.next_absence.label} ab {safeFormatDate(summary.next_absence.start_date, 'd. MMM yyyy')}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {summary.next_absence.working_days} {summary.next_absence.working_days === 1 ? 'Tag' : 'Tage'}
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
            </div>
          </motion.div>
        )}
      </div>

      {/* G) Status Line */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className={`p-4 rounded-lg border flex items-center gap-3 ${statusInfo.bgClass}`}
      >
        <StatusIcon className={`w-5 h-5 ${statusInfo.colorClass}`} />
        <div>
          <p className={`font-medium ${statusInfo.colorClass}`}>{statusInfo.title}</p>
          <p className="text-sm text-muted-foreground">{statusInfo.description}</p>
        </div>
      </motion.div>

      {/* H) History Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="card-glass p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-heading font-semibold text-foreground">Verlauf</h2>
          
          {isCurrentYear && (
            <div className="flex gap-1 bg-muted/30 rounded-lg p-1">
              <button
                type="button"
                onClick={() => setHistoryView('month')}
                className={`px-3 py-1 text-sm rounded ${historyView === 'month' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                Monat
              </button>
              <button
                type="button"
                onClick={() => setHistoryView('year')}
                className={`px-3 py-1 text-sm rounded ${historyView === 'year' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                Jahr
              </button>
            </div>
          )}
        </div>
        
        {historyLoading ? (
          <Skeleton className="h-48 w-full" />
        ) : (
          <>
            {(historyView === 'month' ? chartData.daily : chartData.monthly).filter(d => d.balance !== null).length === 0 ? (
              <div className="h-48 flex items-center justify-center text-muted-foreground">
                Noch keine Verlaufsdaten
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart
                  data={historyView === 'month' ? chartData.daily : chartData.monthly}
                  margin={{ top: 10, right: 64, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgb(16, 185, 129)" stopOpacity={0.3} />
                      <stop offset={`${gradientOffset * 100}%`} stopColor="rgb(16, 185, 129)" stopOpacity={0.1} />
                      <stop offset={`${gradientOffset * 100}%`} stopColor="rgb(249, 115, 22)" stopOpacity={0.1} />
                      <stop offset="100%" stopColor="rgb(249, 115, 22)" stopOpacity={0.3} />
                    </linearGradient>
                    <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgb(16, 185, 129)" />
                      <stop offset={`${gradientOffset * 100}%`} stopColor="rgb(16, 185, 129)" />
                      <stop offset={`${gradientOffset * 100}%`} stopColor="rgb(249, 115, 22)" />
                      <stop offset="100%" stopColor="rgb(249, 115, 22)" />
                    </linearGradient>
                  </defs>
                  
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                    tickFormatter={(v) => `${v}h`}
                  />
                  
                  <Tooltip content={<CustomTooltip />} />
                  
                  {/* Reference lines */}
                  <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeOpacity={0.5} />
                  <ReferenceLine
                    y={capHours}
                    stroke="hsl(var(--muted-foreground))"
                    strokeDasharray="4 4"
                    strokeOpacity={0.3}
                    label={{ value: 'Obergrenze', position: 'right', fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <ReferenceLine
                    y={-lowerCap}
                    stroke="hsl(var(--muted-foreground))"
                    strokeDasharray="4 4"
                    strokeOpacity={0.3}
                    label={{ value: 'Untergrenze', position: 'right', fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  />
                  
                  <Area
                    type="monotone"
                    dataKey="balance"
                    stroke="url(#lineGradient)"
                    strokeWidth={2}
                    fill="url(#areaGradient)"
                    connectNulls={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </>
        )}
      </motion.div>

      {/* ========== EXISTING SECTIONS (UNCHANGED) ========== */}

      {/* Overtime Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card-glass p-6"
      >
        <h2 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          Arbeitszeitkonto-Aktionen
        </h2>
        
        <div className="flex flex-wrap gap-3">
          <Button
            type="button"
            onClick={() => openRequestDialog('payout')}
            disabled={balance <= 0}
            className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30"
            data-testid="request-payout-btn"
          >
            <Banknote className="w-4 h-4 mr-2" />
            Auszahlung beantragen
          </Button>
        </div>

        <p className="text-xs text-muted-foreground mt-3">
          Zeitguthaben baust du über einen Antrag auf <span className="text-blue-400">Zeitausgleich</span> unter Abwesenheiten ab.
        </p>

        {balance <= 0 && (
          <p className="text-sm text-muted-foreground mt-2">
            Du hast aktuell kein verfügbares Zeitguthaben zum Beantragen.
          </p>
        )}
      </motion.div>

      {/* Pending Requests */}
      {pendingRequests.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="card-glass p-6"
        >
          <h2 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            Ausstehende Anträge
          </h2>
          
          <div className="space-y-3">
            {pendingRequests.map((request) => (
              <div key={request.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    request.type === 'reduction' ? 'bg-blue-500/20' : 'bg-amber-500/20'
                  }`}>
                    {request.type === 'reduction' 
                      ? <ArrowDownRight className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      : <Banknote className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    }
                  </div>
                  <div>
                    <p className="text-foreground font-medium">
                      {request.type === 'reduction' ? 'Zeitausgleich' : 'Auszahlung Zeitguthaben'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {Number(request.hours).toFixed(2)}h{request.note ? ` – ${request.note}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getStatusBadge(request.status)}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCancelRequest(request.id)}
                    className="text-muted-foreground hover:text-orange-600 dark:hover:text-orange-400"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Request History */}
      {completedRequests.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card-glass p-6"
        >
          <h2 className="text-lg font-heading font-semibold text-foreground mb-4">
            Meine Anträge
          </h2>
          
          <div className="space-y-2">
            {completedRequests.map((request) => (
              <div key={request.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  {request.type === 'reduction' 
                    ? <ArrowDownRight className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    : <Banknote className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  }
                  <span className="text-sm text-muted-foreground">
                    {safeFormatDate(request.created_at, 'd. MMM yyyy')}
                  </span>
                  <span className="text-foreground font-medium">{Number(request.hours).toFixed(2)}h</span>
                  <span className="text-muted-foreground text-sm">
                    {request.type === 'reduction' ? 'Abbau' : 'Auszahlung'}
                  </span>
                </div>
                {getStatusBadge(request.status)}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Request Dialog - Payout only */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              Auszahlung von Zeitguthaben beantragen
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Beantrage eine Auszahlung deines Zeitguthabens.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-indigo-500/10 rounded-lg">
              <p className="text-sm text-indigo-700 dark:text-indigo-300">
                Verfügbares Zeitguthaben: <span className="font-bold">{balance.toFixed(2)}h</span>
              </p>
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Stunden *</label>
              <Input
                type="number"
                step="0.5"
                min="0.5"
                max={balance}
                value={requestHours}
                onChange={(e) => setRequestHours(e.target.value)}
                placeholder="z.B. 8"
                className="bg-input border-border text-foreground"
                data-testid="request-hours-input"
              />
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Notiz (optional)</label>
              <Input
                value={requestNote}
                onChange={(e) => setRequestNote(e.target.value)}
                placeholder="z.B. Gleittag am 15.03."
                className="bg-input border-border text-foreground"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowRequestDialog(false)}
              className="text-muted-foreground"
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={handleSubmitRequest}
              disabled={isSubmitting || !requestHours}
              className="bg-indigo-500 hover:bg-indigo-600"
              data-testid="submit-request-btn"
            >
              {isSubmitting ? 'Wird eingereicht...' : 'Antrag einreichen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
